import {
    ErrorHandler,
    Injectable
} from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { faro } from '@grafana/faro-web-sdk'

const LazyChunkReloadStorageKey = 'ewc.lazy-chunk-reload-attempted-at'
const LazyChunkReloadRetryMs = 5 * 60 * 1000
const LazyChunkErrorPatterns = [
    /Failed to fetch dynamically imported module/i,
    /Importing a module script failed/i,
    /error loading dynamically imported module/i,
    /Loading chunk .+ failed/i
]

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
    handleError(error: unknown) {
        const normalizedError = this.normalizeError(error)

        if (this.isLazyChunkLoadError(normalizedError.message)) {
            this.reloadOnceAfterLazyChunkError()
        }

        faro.api?.pushError(normalizedError)
        console.error(normalizedError)
    }

    private normalizeError(error: unknown): Error {
        if (error instanceof HttpErrorResponse) {
            return new Error(`Unhandled HTTP error. Status:${error.status} - ${error.statusText}`)
        }

        if (error instanceof Error) {
            return error
        }

        return new Error(`Unhandled application error: ${this.safeSerialize(error)}`)
    }

    private isLazyChunkLoadError(message: string): boolean {
        return LazyChunkErrorPatterns.some(pattern => pattern.test(message))
    }

    private safeSerialize(value: unknown): string {
        const seen = new WeakSet<object>()

        try {
            const serialized = JSON.stringify(value, (key, item) => {
                const normalizedKey = key.toLowerCase()
                if (this.isSensitiveKey(normalizedKey)) {
                    return '[REDACTED]'
                }
                if (typeof item === 'string') {
                    return this.sanitizeString(item)
                }
                if (typeof item === 'bigint') {
                    return item.toString()
                }
                if (item && typeof item === 'object') {
                    if (seen.has(item)) {
                        return '[Circular]'
                    }
                    seen.add(item)
                }
                return item
            })

            return serialized ? this.sanitizeString(serialized) : String(value)
        } catch {
            return Object.prototype.toString.call(value)
        }
    }

    private isSensitiveKey(key: string): boolean {
        return /authorization|cookie|password|secret|token|api[-_]?key|signature/i.test(key) || key === 'sig' || key === 'sas'
    }

    private sanitizeString(value: string): string {
        const safeValue = /^https?:\/\//i.test(value) ? value.split('?')[0].split('#')[0] : value
        return safeValue.length > 2000 ? `${safeValue.slice(0, 2000)}?` : safeValue
    }

    private reloadOnceAfterLazyChunkError(): void {
        if (typeof window === 'undefined' || !window.sessionStorage) {
            return
        }

        const now = Date.now()
        const previousAttemptAt = Number(window.sessionStorage.getItem(LazyChunkReloadStorageKey) || 0)

        if (previousAttemptAt && now - previousAttemptAt < LazyChunkReloadRetryMs) {
            return
        }

        window.sessionStorage.setItem(LazyChunkReloadStorageKey, String(now))
        window.location.reload()
    }
}
